import h from 'hyperscript';
import $ from 'jquery';
import 'jquery-ui/ui/widget';
import 'jquery-ui/ui/widgets/mouse';
import 'jquery-ui/ui/position';
import 'jquery-ui/ui/plugin';
import 'jquery-ui/ui/widgets/draggable';
import 'jquery-ui/ui/widgets/resizable';
import 'jquery-ui-touch-punch/jquery.ui.touch-punch';
import editIcon from '@disy/cadenza-icons/edit.svg';
import folderIcon from '@disy/cadenza-icons/folder.svg';
import infoIcon from '@disy/cadenza-icons/info.svg';
import layerIcon from '@disy/cadenza-icons/map-layer.svg';
import refreshIcon from '@disy/cadenza-icons/refresh.svg';
import settingsIcon from '@disy/cadenza-icons/settings.svg';

import { isEventInsideRect } from 'cadenza/utils/event-util';
import { InfoDialog } from 'cadenza/legacy-dialog/info-dialog';
import { assert } from 'cadenza/utils/custom-error';
import { commonI18n as i18n } from 'cadenza/utils/i18n/i18n';
import { icon } from 'cadenza/utils/icon/icon';
import { supportsTouch, mediaXsOnly } from 'cadenza/utils/media';
import { px } from 'cadenza/utils/px';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';

import './legacy-dialog.css';

const ELEMENT_NAME = 'd-legacy-dialog';

/*
 * Random collection of icons, which are used in legacy dialogs
 */
const ICONS = {
  'icon-edit-attribute': editIcon,
  'icon-folder': folderIcon,
  'icon-info-round': infoIcon,
  'icon-layer': layerIcon,
  'icon-refresh': refreshIcon,
  'wpsDialogTitleIcon': settingsIcon
};

/**
 * This dialog is a temporary solution to get rid of the RichFaces dialog.
 *
 * @deprecated - Use {@link Modal} instead.
 */
class LegacyDialog extends InfoDialog {

  constructor (options) {
    super();
    this._options = options;
    this.dialog.classList.add(ELEMENT_NAME);

    this.dialog.addEventListener('pointerup', event => {
      const target = event.target;
      const dialog = this.dialog;
      if (target === dialog && !isEventInsideRect(event, dialog.getBoundingClientRect())) {
        this.backdropClick = true;
      }
    });

    this.addEventListener('close', () => this.onClosed());
  }

  cancel () {
    if (this.backdropClick) {
      this.backdropClick = false;
    } else {
      super.cancel();
    }
  }

  connectedCallback () {
    super.connectedCallback();
    if (!this._options) {
      // LegacyDialog created in xhtml
      // attr only available in the end of DOM creation
      setTimeout(() => {
        // save attributes into this._options
        this._options = {
          closeable: this.getAttribute('closeable') !== 'false',
          content: this.querySelector('.' + ELEMENT_NAME + '--content'),
          dialogTitleIcon: this.hasAttribute('dialogTitleIcon') ? this.getAttribute('dialogTitleIcon').trim() : undefined,
          id: this.id,
          resizeable: this.resizeable,
          fullScreenOnSmallDevices: false,
          title: this.getAttribute('dialogTitle'),
          autosized: this.autosized
        };
        this._options.width = this.getAttribute('width');
        if (Number.isInteger(parseInt(this._options.width))) {
          this._options.width = parseInt(this._options.width);
        }
        this.removeAttribute('width');
        this._options.height = this.getAttribute('height');
        if (Number.isInteger(parseInt(this._options.height))) {
          this._options.height = parseInt(this._options.height);
        }
        this.removeAttribute('height');
        this._options.classes = this.getAttribute('class');
        this.removeAttribute('class');

        if (this.hasAttribute('onClose')) {
          this._options.onClose = this.getAttribute('onClose');
          this.removeAttribute('onClose');
        }
        if (this.hasAttribute('showPanelControls')) {
          this._options.showPanelControls = this.getAttribute('showPanelControls') !== 'false';
        }

        this._init();
      });
    } else {
      // LegacyDialog created in js, this._options already set
      this.content = document.createElement('div');

      this._init();
    }
  }

  _init () {
    const dialog = this.dialog;
    const options = this._options;
    options.id = _escapeInvalidCharacters(options.id);
    this.id = options.id;

    if (options.classes) {
      dialog.className += ' ' + options.classes;
    }

    // set dialogTitle
    this.dialogTitle = options.title || '';

    // set the content
    if (options.content) {
      if (typeof options.content === 'string') {
        // eslint-disable-next-line no-unsanitized/property
        this.content.innerHTML = options.content; // bad, but - you know - it's legacy
      } else {
        this.content = options.content;
      }
    } else if (options.url) {
      this.content = _createRemoteContent(this._options);
    } else {
      this.content.hidden = true;
    }

    this.content.classList.add('d-legacy-dialog--content');
    if (options.contentClassName) {
      this.content.classList.add(options.contentClassName);
    }
    this.content.classList.add(`${ELEMENT_NAME}--content`);

    // Icon
    const dialogTitleIcon = options.dialogTitleIcon;
    if (dialogTitleIcon) {
      assert(dialogTitleIcon in ICONS, `Icon "${dialogTitleIcon}" is not supported`);
      this.dialog
        .querySelector(`.${InfoDialog.elementName}--title`)
        .before(icon(ICONS[dialogTitleIcon]));
    }

    if (options.showPanelControls) {
      this.dialog.appendChild(_createPanelControls(this._options));
    }
    this.dialog.classList.toggle('with-footer', options.showPanelControls);

    // closable
    this.closeable = options.closeable === undefined || options.closeable;

    if (options.containerClass) {
      this.classList.add(options.containerClass);
    }
    if (options.fullScreenOnSmallDevices) {
      this.classList.add('fullscreen-on-small-screens');
    }

    if (!supportsTouch) {
      // set draggable
      $(this.dialog).draggable({
        containment: '.d-page--content',
        handle: dialog.querySelector('header'),
        stop: (event, ui) => {
          this.adjustMaxHeight();
          Object.assign(options, ui.position);
        }
      }).css('position', 'absolute');

      // set resizable
      if (options.resizeable) {
        $(this.dialog).resizable({
          containment: '.d-page--content',
          resize: (event, ui) => {
            $(this.content).outerHeight(
              ui.size.height
              - $(this).find('header').outerHeight()
              - $(this).find('.panelControls').outerHeight()
            );
          }
        });
      }
    }

    if (options.onClose) {
      // We use a <dialog> polyfill in IE, which does not support the "onclose" attribute.
      // So we use the Function constructor to call addEventListener() instead.
      // eslint-disable-next-line no-new-func
      dialog.addEventListener('close', new Function(options.onClose));
    }
    this.hidden = false;
  }

  adjustMaxHeight () {
    const isVisible = this.dialog.offsetHeight || this.dialog.offsetWidth;
    if (isVisible) {
      let dialogTop = this.dialog.getBoundingClientRect().top;
      let dialogBottom = dialogTop + this.dialog.offsetHeight;

      let content = document.querySelector('.d-page--content');
      let contentTop = content.getBoundingClientRect().top;
      let contentBottom = contentTop + content.offsetHeight;

      if (dialogTop < contentTop || dialogBottom > contentBottom) {
        this.dialog.style.top = px(contentTop);
      }
      let dialogContentTop = this.content.getBoundingClientRect().top;
      let dialogContentTopToContent = dialogContentTop - contentTop;
      this.content.style.maxHeight = px(content.offsetHeight - dialogContentTopToContent);
    }
  }

  showDefault () {
    this.show();
    _setSizeAndPosition(this, this._options);
  }

  get closeable () {
    return !this.dialog.classList.contains('d-legacy-dialog--not-closable');
  }

  set closeable (value) {
    if (value) {
      this.dialog.classList.remove('d-legacy-dialog--not-closable');
      this.dialog.querySelector('header>.button.cancel').hidden = false;
    } else {
      this.dialog.classList.add('d-legacy-dialog--not-closable');
      this.dialog.querySelector('header>.button.cancel').hidden = true;
    }
  }

  get resizeable () {
    return this.hasAttribute('resizeable') ? this.getAttribute('resizeable') !== 'false' : false;
  }

  get autosized () {
    return this.getAttribute('autosized') !== 'false';
  }

  onClosed () {
    if (_isAuxiliaryWindow(this)) {
      delete this._options.top;
      delete this._options.left;
    }
  }

}

customElements.define(ELEMENT_NAME, LegacyDialog);
// =============================================================================
// Exports

/**
 * Shows a window.
 *
 * If the parameter id is specified in the options, a new element with this id will be created and subsequently re-used.
 *
 * Positioning:
 *
 * There are 2 ways to specify positioning.
 * The first and preferred way it to specify a 'positioning' object property in the options, that follows jQuery
 * positioning API (see https://api.jqueryui.com/position/)
 * If a position object is specified it will always be used.
 * In order to position the dialog using X and Y coordinates and without having to create an anchor
 * element, use the function fakeMouseEventForPositioning to create a dummy event with the needed
 * coordinates, and use it for the 'of' property of the positioning object.
 *
 * The second option is to specify the top, bottom, left and right properties on the options. These
 * properties correspond to the CSS properties of the same name for absolute positioning.
 * Note that when using direct CSS positioning like that, there is no check of the boundaries and overflow.
 *
 * Finally, if no position is specified, the dialog will be positioned using jQuery UI, in the middle of the html body element.
 *
 * @param options - Options for the dialog
 * @return {LegacyDialog} The LegacyDialog element shown.
 */
export function showLegacyDialog (options) {
  if (!options.id) {
    options.id = 'nonModalWindow';
  }
  options.id = _escapeInvalidCharacters(options.id);

  let dialog = document.getElementById(options.id);

  if (dialog) {
    if (!options.content && !options.url) {
      dialog.showDefault();
      return;
    } else {
      // do not back up any options if 'nonModalwindow'
      if (!_isAuxiliaryWindow(options) && dialog._options) {
        options = _backUpOptions(options, dialog._options);
      }
      dialog.remove();
    }
  }
  dialog = new LegacyDialog(options);
  document.body.appendChild(dialog);
  dialog.showDefault();
  return dialog;
}

if (!window.disyCommons) {
  window.disyCommons = {};
}

window.disyCommons.showPresizedLegacyDialog = showPresizedLegacyDialog;
window.disyCommons.showLegacyDialog = showLegacyDialog; // CADENZA-40843 Please do not remove assigning that method to disyCommons, because it's needed for some CustomFeatureInfo scripts/templates at customers

export function showPresizedLegacyDialog (options) {
  showLegacyDialog(_presizeWindow(options));
}

/**
 * JQuery UI positioning accepts a mouse event with pageX and pageY properties.
 * See: https://api.jqueryui.com/position/
 * This helper function builds a fake mouse event that can be used to position the dialog relative
 * to any x and y coordinates.
 *
 * @param x - X coordinate
 * @param y - Y coordinate
 * @return {jQuery.Event} An event that can be used to position a dialog.
 */
export function fakeMouseEventForPositioning (x, y) {
  const event = $.Event('click', {
    pageX: x,
    pageY: y
  });
  return event;
}

// =============================================================================
// Private functions

function _presizeWindow (options) {
  if (typeof options.width === 'undefined' || options.width == null || options.width < 0) {
    if (
      mediaXsOnly.matches
      && window.innerWidth > 50
    ) {
      options.width = window.innerWidth - 50;
    } else {
      options.width = 900;
    }
  }
  if (typeof options.height === 'undefined' || options.height == null || options.height < 0) {
    if (
      mediaXsOnly.matches
      && window.innerWidth / 2 > 50
    ) {
      options.height = window.innerWidth / 2 - 50;
    } else {
      options.height = 600;
    }
  }
  return options;
}

function _createRemoteContent (options) {
  return h(
    'iframe.remoteContent',
    {
      frameborder: '0',
      src: cadenzaUrl(options.url) || '',
      name: 'internalFrameRemoteContent',
      style: { height: '100%', width: '100%' }
    },
    h('p', i18n('dialog.noEmbeddedFrames'))
  );
}

function _createPanelControls (options) {
  const panel = h(
    'div.panelControls',
    h('input.cancel', {
      type: 'button',
      value: i18n('close')
    })
  );
  return panel;
}

function _escapeInvalidCharacters (id) {
  return id.replace(/(:|\.)/g, '\\$1');
}

function _position (options) {
  if (options.bottom) {
    options.top = 'auto';
  } else {
    options.top = Math.max(Math.floor((document.body.clientHeight - options.height) / 2), 0);
  }

  if (options.right) {
    options.left = 'auto';
  } else {
    options.left = Math.max(Math.floor((document.body.clientWidth - options.width) / 2), 0);
  }
  return options;
}

function _isAuxiliaryWindow (window) {
  return window.id === 'nonModalWindow';
}

function _setSizeAndPosition (legacyDialog, options) {
  const $dialog = $(legacyDialog.dialog);

  // Size, Position
  $dialog.css('minWidth', 70); // default min value
  $dialog.css('minHeight', 96); // default min value

  legacyDialog.dialog.style.margin = '';

  if (options.autosized || options.autoWidth) {
    $dialog.width('auto');
    legacyDialog.content.style.width = 'auto';
  } else if (options.width && options.width !== '0px' && options.width !== 0) {
    $dialog.width(options.width);
  }
  if (options.autosized || options.autoHeight) {
    $dialog.height('auto');
  } else if (options.height && options.height !== '0px' && options.height !== 0) {
    $dialog.height(options.height);
  }

  const hasPositioning = !!options.positioning;
  if (hasPositioning) {
    $dialog.position(options.positioning);
  } else if (_isAuxiliaryWindow(legacyDialog)) {
    $dialog.css(_position(options));
  } else {
    $dialog.position({
      my: 'center',
      at: 'center',
      of: 'body'
    });
  }
}

function _backUpOptions (newOptions, oldOptions) {
  delete oldOptions.id;
  delete oldOptions.content;
  delete oldOptions.url;
  delete oldOptions.keepLocation;

  if (!newOptions.keepLocation) {
    delete oldOptions.top;
    delete oldOptions.left;
  }

  return Object.assign({}, oldOptions, newOptions);
}
